/**
 * The app shell.
 *
 * Gates on a fluidvip session, then holds the data every screen reads —
 * characters, the library, the gallery, the spend — in one place and reloads it
 * when something changes. At this size a store would be ceremony; when a second
 * consumer of this state appears, that's the moment to add one.
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth, AUTH_CALLBACK_PATH } from './lib/auth';
import { api, type LibraryItem, type Render, type Character, type Swatch, type PhotoSet } from './lib/api';
import LoginScreen from './components/auth/LoginScreen';
import Rail, { type ScreenId } from './components/Rail';
import Studio from './screens/Studio';
import Characters from './screens/Characters';
import Sets from './screens/Sets';
import Library from './screens/Library';
import Gallery from './screens/Gallery';
import Settings from './screens/Settings';
import './styles/shell.css';

interface SpendDay { day: string; total: number; images: number }

export default function App() {
  const { ready, configured, session } = useAuth();
  const [screen, setScreen] = useState<ScreenId>('studio');
  const [collapsed, setCollapsed] = useState(false);

  const [characters, setCharacters] = useState<Character[]>([]);
  const [library, setLibrary] = useState<LibraryItem[]>([]);
  const [renders, setRenders] = useState<Render[]>([]);
  const [spend, setSpend] = useState<SpendDay[]>([]);
  const [sets, setSets] = useState<PhotoSet[]>([]);
  const [activeCharacterId, setActiveCharacterId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingFromLibrary, setPendingFromLibrary] = useState<{ item: LibraryItem; swatch: Swatch } | null>(null);

  const signedIn = !configured || Boolean(session);

  /* Google returns to /auth/callback; the SDK exchanges the code, then we tidy. */
  useEffect(() => {
    if (window.location.pathname === AUTH_CALLBACK_PATH && session) {
      window.history.replaceState({}, '', '/');
    }
  }, [session]);

  const reload = useCallback(async () => {
    if (!signedIn) return;
    try {
      const [s, l, r, sp, st] = await Promise.all([
        api.get<{ characters: Character[] }>('/characters'),
        api.get<{ items: LibraryItem[] }>('/library'),
        api.get<{ renders: Render[] }>('/renders?limit=60'),
        api.get<{ days: SpendDay[] }>('/renders/stats/spend'),
        api.get<{ sets: PhotoSet[] }>('/sets'),
      ]);
      setCharacters(s.characters);
      setLibrary(l.items);
      setRenders(r.renders);
      setSpend(sp.days);
      setSets(st.sets);
      setLoadError(null);
      setActiveCharacterId((prev) => prev ?? s.characters[0]?.id ?? null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Could not reach the API');
    }
  }, [signedIn]);

  useEffect(() => { void reload(); }, [reload]);

  if (!ready) {
    return <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}><span className="spin" /></div>;
  }
  if (!signedIn) return <LoginScreen />;

  const today = new Date().toISOString().slice(0, 10);
  const spentToday = spend.find((d) => d.day === today)?.total ?? 0;

  return (
    <div className={`app${collapsed ? ' collapsed' : ''}`}>
      <Rail
        current={screen}
        onNavigate={setScreen}
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((c) => !c)}
        counts={{ characters: characters.length, sets: sets.length, library: library.length, gallery: renders.length }}
        spentToday={spentToday}
      />

      <div className="main">
        <div className="topbar">
          <div className="crumb">
            <span>{screen[0]!.toUpperCase() + screen.slice(1)}</span>
            {screen === 'studio' && activeCharacterId && (
              <>
                <span className="faint">/</span>
                <b>{characters.find((s) => s.id === activeCharacterId)?.name}</b>
              </>
            )}
          </div>
          <div className="topbar-right">
            {loadError && <span className="pill bad"><span className="dot" />{loadError}</span>}
          </div>
        </div>

        {/* Always mounted, only hidden. Unmounting it threw away the whole
            composition — inputs, prompt, the batch mid-poll — every time you
            looked at another tab. */}
        <Studio
          characters={characters}
          library={library}
          activeCharacterId={activeCharacterId}
          onCharacterChange={setActiveCharacterId}
          onRendered={() => void reload()}
          hidden={screen !== 'studio'}
          pendingInput={pendingFromLibrary}
          onPendingConsumed={() => setPendingFromLibrary(null)}
        />
        {screen === 'characters' && (
          <Characters
            characters={characters}
            onChanged={() => void reload()}
            onUse={(id) => { setActiveCharacterId(id); setScreen('studio'); }}
          />
        )}
        {screen === 'sets' && (
          <Sets characters={characters} onRan={() => void reload()} />
        )}
        {screen === 'library' && (
          <Library
            items={library}
            onChanged={() => void reload()}
            onUse={(item, swatch) => { setPendingFromLibrary({ item, swatch }); setScreen('studio'); }}
          />
        )}
        {screen === 'gallery' && (
          <Gallery renders={renders} onReuse={() => setScreen('studio')} />
        )}
        {screen === 'settings' && <Settings spend={spend} />}
      </div>
    </div>
  );
}
